"use client";

import type { ColumnDef } from "./types";
import { Modal } from "../components/Modal";
import { Button } from "../components/Button";
import { isColumnLocked } from "./useDataTableState";

export interface ColumnSettingsModalProps<T> {
  open: boolean;
  onClose: () => void;
  columns: ColumnDef<T>[];
  onToggleVisible: (key: string, visible: boolean) => void;
  onMove: (key: string, direction: -1 | 1) => void;
  onReset: () => void;
}

/** 裁決 6-3：DataTable 欄位個人化 —— 顯示/隱藏、順序調整。 */
export function ColumnSettingsModal<T>({
  open,
  onClose,
  columns,
  onToggleVisible,
  onMove,
  onReset,
}: ColumnSettingsModalProps<T>) {
  const configurable = columns;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="欄位設定"
      footer={
        <>
          <Button variant="ghost" onClick={onReset}>
            還原預設
          </Button>
          <Button variant="primary" onClick={onClose}>
            完成
          </Button>
        </>
      }
    >
      <div className="cui-dt-colsettings-list">
        {configurable.map((col, idx) => {
          // 第 18 輪一般修正第 1 條：序號欄（kind:"serial"）結構上一律視同鎖定——
          // 比照舊站 DynaGrid SerialColumn 本來就不給隱藏；即使呼叫端欄位定義
          // 漏寫 lockedVisible，也不允許在此把序號欄勾掉（裁決 6-3）。
          const locked = isColumnLocked(col);
          // 第 20 輪嚴重修正第 1 條：「鎖定」不只鎖可見性，也鎖順序——序號欄自己的
          // ↑↓ 鍵要 disabled，且相鄰資料欄朝鎖定欄方向的移動鍵也要 disabled（否則
          // 仍可從對側把序號欄擠開），與 useDataTableState.moveColumn() 的結構性
          // 防呆保持 UI／邏輯層一致（裁決 6-3）。
          const upDisabled =
            locked || idx === 0 || isColumnLocked(configurable[idx - 1]);
          const downDisabled =
            locked || idx === configurable.length - 1 || isColumnLocked(configurable[idx + 1]);
          return (
            <div className="cui-dt-colsettings-row" key={col.key}>
              <input
                type="checkbox"
                checked={col.visible !== false}
                disabled={locked}
                onChange={(e) => onToggleVisible(col.key, e.target.checked)}
                aria-label={`顯示欄位 ${col.label}`}
              />
              <span className="cui-dt-colsettings-label">{col.label}</span>
              <div className="cui-dt-colsettings-moves">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={upDisabled}
                  onClick={() => onMove(col.key, -1)}
                  aria-label={`${col.label} 上移`}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={downDisabled}
                  onClick={() => onMove(col.key, 1)}
                  aria-label={`${col.label} 下移`}
                >
                  ↓
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
